// Order Type Selector component
import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaTruck, FaCar } from "react-icons/fa";
import type { CartTheme, OrderType } from "../types";

interface OrderTypeSelectorProps {
    orderType: OrderType;
    setOrderType: (type: OrderType) => void;
    theme?: CartTheme | null;
}

export const OrderTypeSelector: React.FC<OrderTypeSelectorProps> = ({
    orderType,
    setOrderType,
    theme,
}) => {
    const { t } = useTranslation();

    const options = [
        { type: "delivery" as OrderType, icon: FaTruck, label: t("menu.cart.delivery") },
        { type: "pickup" as OrderType, icon: FaCar, label: t("menu.cart.pickup") },
    ];

    // Use theme colors with sensible defaults
    const primaryColor = theme?.primaryColor || "#16a34a";
    const textColor = theme?.textColor || "#1f2937";
    const borderColor = theme?.borderColor || "#e5e7eb";

    return (
        <div className="pt-3">
            <label
                className="block text-sm font-medium mb-3"
                style={{ color: textColor }}
            >
                {t("menu.cart.orderType")}
            </label>
            
            {/* Segmented Control Container */}
            <div 
                className="relative flex rounded-xl p-1"
                style={{
                    backgroundColor: borderColor,
                }}
            >
                {options.map(({ type, icon: Icon, label }) => {
                    const isSelected = orderType === type;

                    return (
                        <motion.button
                            key={type}
                            type="button"
                            onClick={() => setOrderType(type)}
                            className="relative flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-all duration-300 z-10"
                            style={{
                                backgroundColor: isSelected ? primaryColor : "transparent",
                                color: isSelected ? "#ffffff" : textColor,
                            }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Icon size={18} />
                            <span className="text-sm font-semibold">
                                {label}
                            </span>
                        </motion.button>
                    );
                })}
            </div>
        </div>
    );
};

export default OrderTypeSelector;
